import {
  combineLatest,
  combineLatestWith,
  debounceTime,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap
} from 'rxjs';
import {
  LocationPickerModalComponent,
  LocationPickerResult,
  SelectableMultiCheckboxModalComponent,
  SelectableMultiCheckboxModel,
  SelectableSingleRadioModalComponent,
  SingleRadioItemModel
} from '@ui-components';
import {
  availableLevels,
  DateFormats,
  formatDateToString,
  gameLevelMapper,
  locationTypeMapper,
  AnalyticsService,
  AppRoutes,
  LocationAccessService,
  LocationAnalyticsData,
  LocationStorage,
  FindGamesAnalyticsEvent,
  MODAL_TOKEN,
  ModalProvider,
  NotificationsRoutes,
  SelectedLocation
} from '@core';
import { CalendarHeader, FiltersValues } from '../../../application/models';
import { GetFiltersQueryHandler, SetFilterCommandHandler } from '../../../application/handlers';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  availableFilterDistanceConst,
  availableGameVisibilityConst,
  defaultDistanceMeters,
  distanceShortNameMap,
  gameVisibilityAll,
  gameVisibilityPublicOnly,
  gameVisibilityShortNameMap
} from '../../../application/utils';
import { AppGameLocationType, GameLevelEnum } from '@core-http';
import { DistanceFilterStorage } from '@find-games/infra';
import { Router } from '@angular/router';

export class FiltersViewService {
  private readonly getFiltersQueryHandler: GetFiltersQueryHandler = inject(GetFiltersQueryHandler);
  private readonly setFilterCommandHandler: SetFilterCommandHandler = inject(SetFilterCommandHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly locationStorage: LocationStorage = inject(LocationStorage);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly distanceFilterStorage: DistanceFilterStorage = inject(DistanceFilterStorage);
  private readonly router: Router = inject(Router);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // Okno na ustawienie kilku filtrów pod rząd - liczy się od ostatniej zmiany, potem leci jeden event z kompletem.
  private readonly filtersTrackDelayMs: number = 5000;
  private readonly filtersChanged: Subject<void> = new Subject<void>();
  private readonly pendingFilterChanges: Set<string> = new Set<string>();

  readonly selectedLocation$: Observable<SelectedLocation | null> = this.locationStorage.location$;

  readonly savedFilters$: Observable<FiltersValues> = this.getFiltersQueryHandler.filters().pipe(shareReplay(1));

  readonly availableDistance$: Observable<SingleRadioItemModel<number>[]> = of(availableFilterDistanceConst);

  readonly availableGameVisibility$: Observable<SingleRadioItemModel<string>[]> = of(availableGameVisibilityConst);

  readonly availableGameLevels$: Observable<SelectableMultiCheckboxModel<GameLevelEnum>[]> = of(availableLevels).pipe(
    combineLatestWith(this.savedFilters$),
    take(1),
    map(([gameLevel, filters]: [GameLevelEnum[], FiltersValues]) =>
      gameLevel.map((level: GameLevelEnum) => {
        return {
          name: gameLevelMapper[level],
          value: level,
          isSelected: filters.gameLevel?.includes(level)
        };
      })
    )
  );

  readonly availableLocationTypes$: Observable<SelectableMultiCheckboxModel<AppGameLocationType>[]> = of(
    Object.values(AppGameLocationType)
  ).pipe(
    combineLatestWith(this.savedFilters$),
    take(1),
    map(([locationType, filters]: [AppGameLocationType[], FiltersValues]) =>
      locationType.map((location: AppGameLocationType) => {
        return {
          name: locationTypeMapper[location].name,
          value: location,
          isSelected: filters.locationType?.includes(location)
        };
      })
    )
  );

  constructor() {
    this.filtersChanged
      .pipe(debounceTime(this.filtersTrackDelayMs), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sendFiltersChangeEvent());

    // Opuszczenie ekranu przed upływem okna - wysyłamy to, co user zdążył ustawić.
    this.destroyRef.onDestroy(() => this.sendFiltersChangeEvent());
  }

  initialize(): void {
    const location: SelectedLocation | null = this.locationStorage.location;

    // Ręczna - bez GPS, używamy zapisanej.
    if (location?.source === 'manual') {
      this.setStoredLocationFilter(location);
      return;
    }

    // Auto/brak - próba max raz na 24h.
    this.locationAccessService
      .ensureLocationOnEntry$()
      .pipe(take(1))
      .subscribe((acquired: SelectedLocation | null) => {
        if (acquired) {
          this.applyLocation(acquired);
        } else if (location) {
          // Bramka 24h / błąd - użyj poprzedniej.
          this.setStoredLocationFilter(location);
        }
      });
  }

  openLocationPicker(): void {
    this.trackLocationFilterClick();

    this.modalProvider
      .showModal$<LocationPickerResult>({
        component: LocationPickerModalComponent,
        cssClass: 'location-picker-modal',
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        componentProps: { selected: this.locationStorage.location }
      })
      .pipe(take(1))
      .subscribe((result: LocationPickerResult | undefined) => {
        if (!result) return;

        if (result.kind === 'auto') {
          this.locationAccessService
            .ensureLocation$()
            .pipe(take(1))
            .subscribe((acquired: SelectedLocation | null) => {
              if (acquired) this.applyLocation(acquired);
            });
          return;
        }

        this.applyLocation(result.location);
      });
  }

  openDistanceFilter(): void {
    const location: SelectedLocation | null = this.locationStorage.location;
    if (!location) return;

    combineLatest([this.availableDistance$, this.savedFilters$])
      .pipe(
        take(1),
        switchMap(([availableDistance, filters]: [SingleRadioItemModel<number>[], FiltersValues]) =>
          this.distanceModal(availableDistance, filters)
        ),
        switchMap((distance: number | null) => {
          this.distanceFilterStorage.save(distance ?? undefined);

          return this.setFilterCommandHandler.set({
            maxDistanceMeters: distance ?? undefined,
            latitude: location.coordinates.latitude,
            longitude: location.coordinates.longitude
          });
        }),
        tap(() => this.trackFiltersChange('distance'))
      )
      .subscribe();
  }

  clearLocation(): void {
    this.locationStorage.clearLocation();
    this.setFilterCommandHandler
      .set({ latitude: undefined, longitude: undefined, maxDistanceMeters: undefined })
      .subscribe();
  }

  getDistanceName(value: number): string {
    if (!value) return 'Bez limitu';
    return distanceShortNameMap[value] ?? 'Okolice';
  }

  openGameLevel(): void {
    combineLatest([this.savedFilters$, this.availableGameLevels$])
      .pipe(
        take(1),
        switchMap(([selectedLevels, availableLevels]: [FiltersValues, SelectableMultiCheckboxModel<GameLevelEnum>[]]) =>
          this.gameLevelModal(availableLevels, selectedLevels.gameLevel)
        ),
        switchMap((gameLevels: GameLevelEnum[]) =>
          this.setFilterCommandHandler.set({
            gameLevel: gameLevels
          })
        ),
        tap(() => this.trackFiltersChange('level'))
      )
      .subscribe();
  }

  openLocationType(): void {
    combineLatest([this.savedFilters$, this.availableLocationTypes$])
      .pipe(
        take(1),
        switchMap(
          ([savedFilters, availableLocationTypes]: [
            FiltersValues,
            SelectableMultiCheckboxModel<AppGameLocationType>[]
          ]) => this.locationTypeModal(availableLocationTypes, savedFilters.locationType)
        ),
        switchMap((selectedLocations: AppGameLocationType[]) =>
          this.setFilterCommandHandler.set({
            locationType: selectedLocations
          })
        ),
        tap(() => this.trackFiltersChange('locationType'))
      )
      .subscribe();
  }

  openGameVisibility(): void {
    combineLatest([this.savedFilters$, this.availableGameVisibility$])
      .pipe(
        take(1),
        switchMap(([savedFilters, availableVisibility]: [FiltersValues, SingleRadioItemModel<string>[]]) =>
          this.gameVisibilityModal(availableVisibility, savedFilters.showPrivateBlurred)
        ),
        switchMap((visibility: string) => this.setFilterCommandHandler.set({ showPrivateBlurred: visibility })),
        tap(() => this.trackFiltersChange('visibility'))
      )
      .subscribe();
  }

  getGameVisibilityName(showPrivateBlurred: string): string {
    return gameVisibilityShortNameMap[showPrivateBlurred] ?? gameVisibilityShortNameMap[gameVisibilityAll];
  }

  getGameLevelName(gameLevel: GameLevelEnum[]): string {
    if (!gameLevel?.length) return 'dowolny';

    if (gameLevel?.length === 1) return gameLevelMapper[gameLevel[0]];

    return `${gameLevelMapper[gameLevel[0]]} +${gameLevel?.length - 1}`;
  }

  setDate(data: CalendarHeader): void {
    const specificDate: Date | undefined = data.isSelected ? undefined : data.value;

    this.setFilterCommandHandler
      .set({ specificDate })
      .pipe(take(1))
      .subscribe(() => this.trackFiltersChange('date'));
  }

  goToAlerts(isEnabled: boolean): void {
    this.analyticsService.buttonClick(FindGamesAnalyticsEvent.MapFilterAlerts, { isEnabled });
    this.router.navigateByUrl(`${AppRoutes.NOTIFICATIONS}/${NotificationsRoutes.ALERTS}`);
  }

  resetFilters(): void {
    const location: SelectedLocation | null = this.locationStorage.location;
    this.distanceFilterStorage.clear();

    this.setFilterCommandHandler
      .set({
        specificDate: undefined,
        locationType: [],
        gameLevel: [],
        showPrivateBlurred: gameVisibilityAll,
        maxDistanceMeters: location ? defaultDistanceMeters : undefined,
        latitude: location?.coordinates.latitude,
        longitude: location?.coordinates.longitude
      })
      .pipe(take(1))
      .subscribe(() => this.trackFiltersChange('reset'));
  }

  getLocationTypeName(locationType: AppGameLocationType[]): string {
    if (!locationType?.length) return 'dowolny';

    if (locationType?.length === 1) return locationTypeMapper[locationType[0]].name;

    return `${locationTypeMapper[locationType[0]]?.name} +${locationType?.length - 1}`;
  }

  private trackFiltersChange(changed: string): void {
    this.pendingFilterChanges.add(changed);
    this.filtersChanged.next();
  }

  private sendFiltersChangeEvent(): void {
    if (!this.pendingFilterChanges.size) return;

    const changed: string[] = [...this.pendingFilterChanges];
    this.pendingFilterChanges.clear();

    this.savedFilters$.pipe(take(1)).subscribe((filters: FiltersValues) =>
      this.analyticsService.buttonClick(FindGamesAnalyticsEvent.FiltersChange, {
        changed,
        screen: this.router.url.includes('map') ? 'map' : 'list',
        date: filters.specificDate ? formatDateToString(filters.specificDate, DateFormats.IsoDate) : null,
        gameLevel: filters.gameLevel ?? [],
        locationType: filters.locationType ?? [],
        maxDistanceMeters: filters.maxDistanceMeters ?? null,
        visibility: filters.showPrivateBlurred === gameVisibilityPublicOnly ? 'public-only' : 'all',
        hasLocation: !!filters.latitude && !!filters.longitude
      })
    );
  }

  private trackLocationFilterClick(): void {
    const hasLocation: boolean = !!this.locationStorage.location;

    this.locationAccessService
      .accessState$()
      .pipe(take(1))
      .subscribe((state: LocationAnalyticsData) =>
        this.analyticsService.buttonClick(FindGamesAnalyticsEvent.ListFilterLocation, { ...state, hasLocation })
      );
  }

  private setStoredLocationFilter(location: SelectedLocation): void {
    this.setFilterCommandHandler
      .set({
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude,
        maxDistanceMeters: this.distanceFilterStorage.selectedAsFilterValue
      })
      .subscribe();
  }

  private applyLocation(location: SelectedLocation): void {
    this.locationStorage.setLocation(location).pipe(take(1)).subscribe();

    this.setFilterCommandHandler
      .set({
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude,
        maxDistanceMeters: this.distanceFilterStorage.selectedAsFilterValue
      })
      .subscribe();
  }

  private locationTypeModal(
    availableLocationTypes: SelectableMultiCheckboxModel<AppGameLocationType>[],
    selectedLocationTypes: AppGameLocationType[]
  ): Observable<AppGameLocationType[]> {
    return this.modalProvider
      .showModal$<AppGameLocationType[]>({
        component: SelectableMultiCheckboxModalComponent<AppGameLocationType>,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Typ boiska',
          providedData: availableLocationTypes,
          selectedData: selectedLocationTypes
        }
      })
      .pipe(map((result: AppGameLocationType[]) => result ?? selectedLocationTypes));
  }

  private gameLevelModal(
    availableLevels: SelectableMultiCheckboxModel<GameLevelEnum>[],
    selectedLevels: GameLevelEnum[]
  ): Observable<GameLevelEnum[]> {
    return this.modalProvider
      .showModal$<GameLevelEnum[]>({
        component: SelectableMultiCheckboxModalComponent<GameLevelEnum>,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Poziom gierki',
          providedData: availableLevels,
          selectedData: selectedLevels
        }
      })
      .pipe(map((result: GameLevelEnum[]) => result ?? selectedLevels));
  }

  private gameVisibilityModal(
    availableGameVisibility: SingleRadioItemModel<string>[],
    selectedGameVisibility: string
  ): Observable<string> {
    return this.modalProvider
      .showModal$<string>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Widoczność gierki',
          providedData: availableGameVisibility,
          selectedData: selectedGameVisibility ?? gameVisibilityAll
        }
      })
      .pipe(map((result: string) => result ?? selectedGameVisibility ?? gameVisibilityAll));
  }

  private distanceModal(
    availableDistance: SingleRadioItemModel<number>[],
    filters: FiltersValues
  ): Observable<number | null> {
    return this.modalProvider
      .showModal$<number>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Odległość',
          providedData: availableDistance,
          selectedData: filters.maxDistanceMeters ?? 0
        }
      })
      .pipe(map((result: number) => (result ?? filters.maxDistanceMeters) || null));
  }
}
